import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Préférence de thème : 'light' (Clair), 'dark' (Sombre) ou 'auto' (suit le système).
// Même logique que public/theme-init.js, qui l'applique avant le montage de React
// (aucun flash de thème au rechargement).
export const THEME_STORAGE_KEY = 'digitrans_theme';
const PREFERENCES = ['light', 'dark', 'auto'];

// localStorage peut être indisponible (navigation privée, stockage bloqué)
function readPreference() {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return PREFERENCES.includes(value) ? value : 'auto';
  } catch {
    return 'auto';
  }
}

function writePreference(value) {
  try {
    if (value === 'auto') window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, value);
  } catch {
    /* préférence non mémorisée, le thème reste appliqué pour la session */
  }
}

const media = (query) => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query) : null);

// Thème du système ; sombre par défaut si le système n'exprime aucune préférence (décision §5)
function systemTheme() {
  if (media('(prefers-color-scheme: light)')?.matches) return 'light';
  return 'dark';
}

function systemHasPreference() {
  return !!(media('(prefers-color-scheme: light)')?.matches || media('(prefers-color-scheme: dark)')?.matches);
}

function applyToDocument(preference, { animate }) {
  const root = document.documentElement;
  if (animate) {
    // Transition de couleurs le temps de la bascule seulement (pas sur chaque survol)
    root.classList.add('ds-theme-transition');
    window.clearTimeout(applyToDocument.timer);
    applyToDocument.timer = window.setTimeout(() => root.classList.remove('ds-theme-transition'), 320);
  }
  if (preference === 'light' || preference === 'dark') {
    root.setAttribute('data-theme', preference);
  } else if (systemHasPreference()) {
    // Auto : la media query CSS suit le système, y compris s'il change en direct
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', 'dark');
  }
}

const ThemeContext = createContext({
  preference: 'auto',
  resolved: 'dark',
  setPreference: () => {},
});

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(readPreference);
  const [system, setSystem] = useState(systemTheme);
  // Préférence courante hors rendu : si localStorage est bloqué, relire le stockage
  // rendrait 'auto' et un changement du système écraserait un choix Clair / Sombre
  const preferenceRef = useRef(preference);

  // Filet de sécurité si public/theme-init.js n'a pas pu s'exécuter : la préférence
  // est posée avant le premier affichage de React
  useLayoutEffect(() => {
    applyToDocument(preferenceRef.current, { animate: false });
  }, []);

  // Suivre le thème du système (mode Auto)
  useEffect(() => {
    const queries = [media('(prefers-color-scheme: dark)'), media('(prefers-color-scheme: light)')].filter(Boolean);
    const onChange = () => {
      setSystem(systemTheme());
      if (preferenceRef.current === 'auto') applyToDocument('auto', { animate: true });
    };
    queries.forEach((q) => (q.addEventListener ? q.addEventListener('change', onChange) : q.addListener(onChange)));
    return () => queries.forEach((q) => (q.removeEventListener ? q.removeEventListener('change', onChange) : q.removeListener(onChange)));
  }, []);

  // Synchroniser les onglets ouverts
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === THEME_STORAGE_KEY) {
        const next = PREFERENCES.includes(event.newValue) ? event.newValue : 'auto';
        preferenceRef.current = next;
        setPreferenceState(next);
        applyToDocument(next, { animate: true });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setPreference = useCallback((next) => {
    if (!PREFERENCES.includes(next)) return;
    preferenceRef.current = next;
    setPreferenceState(next);
    writePreference(next);
    applyToDocument(next, { animate: true });
  }, []);

  const value = useMemo(() => ({
    preference,
    resolved: preference === 'auto' ? system : preference,
    setPreference,
  }), [preference, system, setPreference]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** { preference: 'light'|'dark'|'auto', resolved: 'light'|'dark', setPreference } */
export function useTheme() {
  return useContext(ThemeContext);
}
